import { z } from 'zod';
import { colorSchema, gradeSchema, idSchema, mmSchema } from './common.js';
import { fontRefSchema } from './fonts.js';
import { richTextSchema, textEffectSchema } from './text.js';
import { cellSchema, generatorRefSchema, taskConfigSchema, taskDisplaySchema, taskKindSchema, } from './task.js';
// ---------------------------------------------------------------------------
// Gemeinsame Basis (A.4)
// ---------------------------------------------------------------------------
export const boardInteractionSchema = z.object({
    movable: z.boolean(),
    visibleAtStart: z.boolean(),
    toggleOnTap: z.boolean(),
    variants: z.object({ show: z.boolean(), count: z.number().int().min(1) }).optional(),
});
export const differentiationSchema = z.enum(['none', 'basic', 'standard', 'advanced']);
const itemBaseShape = {
    id: idSchema,
    x: mmSchema,
    y: mmSchema,
    width: mmSchema,
    height: mmSchema,
    /** Drehung in Grad, im Uhrzeigersinn um die Feldmitte. */
    rotation: z.number(),
    locked: z.boolean(),
    groupId: idSchema.optional(),
    anchors: z.array(z.object({ guideId: idSchema })).optional(),
    hidden: z
        .object({
        paper: z.boolean().optional(),
        digital: z.boolean().optional(),
        board: z.boolean().optional(),
    })
        .optional(),
    board: boardInteractionSchema.optional(),
    differentiation: differentiationSchema.optional(),
};
export const itemBaseSchema = z.object(itemBaseShape);
export const borderSchema = z.object({
    kind: z.enum(['none', 'line', 'box', 'roundBox', 'dashed']),
    weight: z.number().positive().optional(),
    color: colorSchema.optional(),
    radius: mmSchema.optional(),
});
export const strokeSchema = z.object({
    color: colorSchema,
    weight: z.number().positive(),
    dash: z.enum(['solid', 'dashed', 'dotted']).optional(),
    startCap: z.enum(['none', 'arrow', 'dot']).optional(),
    endCap: z.enum(['none', 'arrow', 'dot']).optional(),
});
export const fillSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    z.object({ kind: z.literal('solid'), color: colorSchema }),
    z.object({
        kind: z.literal('pattern'),
        pattern: z.enum(['hatch', 'cross', 'dots']),
        color: colorSchema,
    }),
    z.object({ kind: z.literal('grid'), step: mmSchema, color: colorSchema }),
]);
// ---------------------------------------------------------------------------
// Text und Textumfeld (A.4-Tabelle)
// ---------------------------------------------------------------------------
const textShape = {
    content: richTextSchema,
    effects: z.array(textEffectSchema).default([]),
    border: borderSchema.optional(),
    lineNumbers: z.boolean().optional(),
    /** Textfluss in ein weiteres Textfeld. */
    flowTo: idSchema.optional(),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
    align: z.enum(['left', 'center', 'right', 'justify']).optional(),
    valign: z.enum(['top', 'middle', 'bottom']).optional(),
    /** Zeilenabstand in Prozent der Schriftgröße (Phase 2.1). */
    lineHeightPercent: z.number().min(80).max(400).optional(),
    /** Innenabstand in mm. */
    padding: mmSchema.optional(),
    /** Höhe folgt dem Inhalt. */
    autoHeight: z.boolean().optional(),
};
export const textItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('text'),
    ...textShape,
});
export const bubbleItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('bubble'),
    ...textShape,
    shape: z.enum(['speech', 'thought']),
    /** Spitze der Sprechblase, relativ zur linken oberen Feldecke in mm. */
    tail: z.object({ x: mmSchema, y: mmSchema }),
    fillBackground: z.boolean(),
});
export const linedTextItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('linedText'),
    grade: gradeSchema,
    lineHeightPercent: z.number().min(100).max(300),
    ratio: z.enum(['auto', 'font', '1:1:1', '1:2:1']),
    /** Füllfarbe der Hauptzeile. */
    rowFill: colorSchema.optional(),
    /** Füllfarbe des Zeilenzwischenraums. */
    gapFill: colorSchema.optional(),
    highlightMainRow: z.boolean(),
    writingHouse: z.enum(['left', 'right', 'both']).optional(),
    content: richTextSchema,
    effects: z.array(textEffectSchema).default([]),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
    /** Innenabstand in mm. */
    padding: mmSchema.optional(),
    /** Höhe folgt der Zeilenzahl. */
    autoHeight: z.boolean().optional(),
});
export const tableCellSchema = z.object({
    content: richTextSchema,
    fill: colorSchema.optional(),
    borders: z.object({
        top: borderSchema.optional(),
        right: borderSchema.optional(),
        bottom: borderSchema.optional(),
        left: borderSchema.optional(),
    }),
    span: z.object({ rows: z.number().int().min(1), cols: z.number().int().min(1) }).optional(),
});
export const tableItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('table'),
    /** Zeilenhöhen in mm. */
    rows: z.array(mmSchema).min(1),
    /** Spaltenbreiten in mm. */
    cols: z.array(mmSchema).min(1),
    cells: z.array(z.array(tableCellSchema)),
    padding: mmSchema,
    /** Rahmen des ganzen Felds (Phase 2.5). */
    border: borderSchema.optional(),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
export const numberingItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('numbering'),
    /** Fortlaufend aus der Seitenreihenfolge berechnen. */
    auto: z.boolean(),
    value: z.string().optional(),
    /** Darstellung der Zahl (Phase 2.6). */
    format: z.enum(['n.', 'n)', '(n)', 'n']).optional(),
    /** Startwert der automatischen Zählung. */
    start: z.number().int().optional(),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
// ---------------------------------------------------------------------------
// Bild, Form, Raster
// ---------------------------------------------------------------------------
export const imageItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('image'),
    assetId: idSchema,
    /** Beschnitt in Anteilen der Bildkanten (0–1). */
    crop: z.object({
        top: z.number().min(0).max(1),
        right: z.number().min(0).max(1),
        bottom: z.number().min(0).max(1),
        left: z.number().min(0).max(1),
    }),
    flip: z.object({ horizontal: z.boolean(), vertical: z.boolean() }),
    recolor: colorSchema.optional(),
    whiteToTransparent: z.boolean(),
    opacity: z.number().min(0).max(1),
    style: z.enum(['color', 'bw', 'outline']),
    border: borderSchema.optional(),
});
export const SHAPE_KINDS = [
    'line',
    'arrow',
    'arc',
    'rect',
    'roundRect',
    'diamond',
    'ellipse',
    'polygon',
    'star',
    'pyramid',
    'fractionCircle',
];
export const shapeItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('shape'),
    kind: z.enum(SHAPE_KINDS),
    stroke: strokeSchema,
    fill: fillSchema,
    /** Formparameter, z. B. { points: 5 } für Stern, { segments, filled } für Bruchkreis. */
    params: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])).default({}),
});
export const gridCellSchema = z.object({
    row: z.number().int().nonnegative(),
    col: z.number().int().nonnegative(),
    state: z.enum(['empty', 'filled', 'crossed']),
    color: colorSchema.optional(),
    label: z.string().optional(),
});
export const gridItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('grid'),
    cellWidth: mmSchema,
    cellHeight: mmSchema,
    display: z.enum(['squares', 'circles', 'octagons', 'dots', 'iso']),
    cells: z.array(gridCellSchema).default([]),
    stroke: strokeSchema.optional(),
    /** Spalten und Zeilen; ohne Angabe aus Feldgröße und Kästchengröße. */
    cols: z.number().int().min(1).optional(),
    rows: z.number().int().min(1).optional(),
    /** Außenrand stärker zeichnen (Phase 2.8). */
    thickBorder: z.boolean().optional(),
    /** Lösungsstrich unter der letzten Zeile. */
    solutionRule: z.enum(['none', 'single', 'double']).optional(),
    /** Füllfarbe der Kästchen. */
    cellColor: colorSchema.optional(),
    /** Schmale Kästchen für Komma und Klammern. */
    narrowColumns: z.array(z.number().int().nonnegative()).optional(),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
// ---------------------------------------------------------------------------
// Code, Medien, Diagramm, Formel
// ---------------------------------------------------------------------------
export const qrItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('qr'),
    content: z.string().min(1),
    border: borderSchema.optional(),
});
export const linkItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('link'),
    url: z.url({ error: 'Bitte eine vollständige Adresse angeben (https://…)' }),
    label: z.string().optional(),
    security: z.enum(['open', 'confirm', 'allowlist']),
});
export const audioItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('audio'),
    /** Vorlesetext (Sprachsynthese) … */
    speechText: z.string().optional(),
    /** … oder eine hinterlegte Audiodatei. */
    soundAssetId: idSchema.optional(),
    lang: z.string().min(2),
    /** Unsichtbare Tippfläche auf dem digitalen Blatt. */
    invisible: z.boolean().default(false),
});
export const chartItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('chart'),
    kind: z.enum(['bar', 'column', 'pie']),
    points: z.array(z.object({ value: z.number(), label: z.string(), color: colorSchema.optional() })),
    axes: z
        .object({
        showX: z.boolean(),
        showY: z.boolean(),
        step: z.number().positive().optional(),
        maxValue: z.number().optional(),
        xLabel: z.string().optional(),
        yLabel: z.string().optional(),
    })
        .optional(),
    legend: z.boolean().default(false),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
export const formulaItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('formula'),
    /** AsciiMath-Quelltext. */
    source: z.string(),
    fontSize: z.number().positive(),
});
// ---------------------------------------------------------------------------
// Aufgabenfeld (A.6)
// ---------------------------------------------------------------------------
export const taskItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('task'),
    kind: taskKindSchema,
    mode: z.enum(['generated', 'manual']),
    config: taskConfigSchema,
    generator: generatorRefSchema,
    resolved: z.array(cellSchema).default([]),
    display: taskDisplaySchema.default({}),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
// ---------------------------------------------------------------------------
// Lösungen und Selbstkontrolle (A.7)
// ---------------------------------------------------------------------------
export const solutionTextItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('solutionText'),
    answer: z.string(),
    accept: z.array(z.string()).optional(),
    caseSensitive: z.boolean().optional(),
    distractors: z.array(z.string()).default([]),
    frame: z.enum(['underline', 'box', 'none']),
    equalWidthOnPage: z.boolean(),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
export const solutionImageItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('solutionImage'),
    correctAssetId: idSchema,
    distractorAssetIds: z.array(idSchema).default([]),
});
export const selfCheckItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('selfCheck'),
    linkedItemIds: z.array(idSchema).default([]),
    motif: z.enum(['snake', 'car', 'hedgehog', 'rect']),
    distractorCount: z.number().int().nonnegative(),
    reseed: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Sprachrätsel
// ---------------------------------------------------------------------------
export const wordSearchItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('wordSearch'),
    cols: z.number().int().min(2),
    rows: z.number().int().min(2),
    difficulty: z.enum(['easy', 'medium', 'hard']),
    words: z.array(z.string().min(1)),
    /** Umlaute als Ä/Ö/Ü oder als AE/OE/UE setzen. */
    umlauts: z.enum(['keep', 'expand']),
    case: z.enum(['upper', 'lower', 'keep']),
    /** Wortliste unter das Rätsel setzen. */
    appendWords: z.boolean(),
    highlightFirst: z.boolean(),
    reseed: z.number().int().nonnegative(),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
export const letterCloudItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('letterCloud'),
    text: z.string().min(1),
    motif: z.string().optional(),
    highlightFirst: z.boolean(),
    colorVariant: z.enum(['none', 'syllables', 'random']),
    reseed: z.number().int().nonnegative().default(0),
    font: fontRefSchema.optional(),
    fontSize: z.number().positive().optional(),
});
/**
 * Piktogramm für Arbeitsaufträge (Phase 8.2): ein Symbol mit Beschriftung,
 * das zeigt, was zu tun ist – schneiden, kleben, malen, lesen …
 */
export const pictogramItemSchema = z.object({
    ...itemBaseShape,
    type: z.literal('pictogram'),
    symbol: z.enum([
        'cut',
        'glue',
        'paint',
        'read',
        'write',
        'listen',
        'speak',
        'partner',
        'group',
        'think',
        'check',
        'computer',
    ]),
    label: z.string().optional(),
    /** Symbolfarbe; ohne Angabe die Textfarbe. */
    color: colorSchema.optional(),
});
// ---------------------------------------------------------------------------
// Union
// ---------------------------------------------------------------------------
export const itemSchema = z.discriminatedUnion('type', [
    textItemSchema,
    bubbleItemSchema,
    linedTextItemSchema,
    tableItemSchema,
    numberingItemSchema,
    imageItemSchema,
    shapeItemSchema,
    qrItemSchema,
    chartItemSchema,
    formulaItemSchema,
    gridItemSchema,
    taskItemSchema,
    solutionTextItemSchema,
    solutionImageItemSchema,
    selfCheckItemSchema,
    wordSearchItemSchema,
    letterCloudItemSchema,
    audioItemSchema,
    linkItemSchema,
    pictogramItemSchema,
]);
/** Feldtypen, die gedreht werden dürfen (Phase 1.2). */
export const ROTATABLE_ITEM_TYPES = [
    'text',
    'bubble',
    'image',
    'shape',
    'qr',
    'formula',
    'letterCloud',
];
export function isRotatable(item) {
    return ROTATABLE_ITEM_TYPES.includes(item.type);
}
//# sourceMappingURL=items.js.map